import React from "react";

type Props = {
  children: React.ReactNode;
  /** Heading shown when an error is caught. Defaults to "Something went wrong". */
  title?: string;
};

type State = {
  hasError: boolean;
  error?: unknown;
};

/**
 * Single app-wide error boundary. Used both at the application root (App.tsx)
 * and around crash-prone admin surfaces (e.g. the page-builder / preview frame)
 * via the optional `title` prop. Theme-aware so it respects dark and light mode.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // Surface to console for debugging.
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Component stack:", info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      this.state.error instanceof Error
        ? this.state.error.message
        : typeof this.state.error === "string"
          ? this.state.error
          : "Unknown error";

    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-2xl rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-display text-foreground">
            {this.props.title ?? "Something went wrong"}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The page crashed while rendering. The details below will help us fix the root cause.
          </p>
          <pre className="mt-4 whitespace-pre-wrap rounded-lg bg-muted p-4 text-xs text-foreground overflow-auto">
            {message}
          </pre>
          <p className="mt-4 text-xs text-muted-foreground">
            Tip: Open DevTools Console to see the full stack trace.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
